import React, { useState, useRef, useEffect } from "react";
import { Helmet } from "react-helmet";
import RevealAnimation from "../RevealAnimation";
import { renderPortableText, portableTextToPlainText } from "../../utils/sanityHelpers";
import "../decor/_decorSquares.scss";
import "./style.scss";

const FAQSection = ({
  data,
  faqs: faqsProp,
  title: titleProp,
  subtitle: subtitleProp,
  disableDecor = false,
}) => {
  const [openSet, setOpenSet] = useState(new Set()); // allow multiple open
  const [searchTerm, setSearchTerm] = useState("");
  const answerRefs = useRef([]);

  // Two ways to feed this section: `data` (Sanity portable-text shape, used
  // by the CMS-driven FAQ pages) or plain `faqs`/`title` props (simple
  // {q, a} strings, used by pages like GrowRyte that hardcode their FAQs).
  const isPlainMode = !data;
  const title = data ? data.title : titleProp;
  const subtitle = data ? data.subtitle : subtitleProp;
  const faqs = ((data ? data.faqs : faqsProp) || []).map((f) => ({
    question: f.question ?? f.q,
    answer: f.answer ?? f.a,
  }));
  const enabled = isPlainMode
    ? faqs.length > 0
    : !!(data?.enabled && faqs.length);

  const toggleFAQ = (index) => {
    setOpenSet((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index); else next.add(index);
      return next;
    });
  };

  const filteredFaqs = faqs.filter((faq) => {
    if (!searchTerm) return true;
    const answerText = isPlainMode
      ? faq.answer
      : portableTextToPlainText(faq.answer);
    const text = `${faq.question} ${answerText}`.toLowerCase();
    return text.includes(searchTerm.toLowerCase());
  });

  // Animate height via inline style for smoother auto-height transitions
  useEffect(() => {
    answerRefs.current.forEach((el, idx) => {
      if (!el) return;
      if (openSet.has(idx)) {
        el.style.maxHeight = el.scrollHeight + "px";
      } else {
        el.style.maxHeight = "0px";
      }
    });
  }, [openSet, filteredFaqs.length]);

  if (!enabled) return null;

  const faqPageJSONLD = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: isPlainMode
          ? faq.answer
          : portableTextToPlainText(faq.answer),
      },
    })),
  };

  return (
    <section className="faq-section redesigned">
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(faqPageJSONLD)}
        </script>
      </Helmet>
      {!disableDecor && (
        <div className="decor-squares" aria-hidden="true">
          <span className="decor-square" style={{ top:32, right:'6%' }} />
          <span className="decor-square lg" style={{ bottom:50, left:'8%' }} />
        </div>
      )}
      <div className="body-container">
        <div className="faq-header-wrapper">
          <RevealAnimation animation="slideUp" className="section-header">
            <h2 className="section-title">
              {title && (
                <>
                  <span className="title-highlight">{title.split(" ")[0]}</span>{" "}
                  {title.split(" ").slice(1).join(" ")}
                </>
              )}
            </h2>
            {subtitle && <p className="section-subtitle">{subtitle}</p>}
          </RevealAnimation>
          <div className="faq-actions">
            <div className="faq-search">
              <input
                type="text"
                placeholder="Search questions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                aria-label="Search FAQs"
              />
              {searchTerm && (
                <button
                  type="button"
                  className="clear-btn"
                  onClick={() => setSearchTerm("")}
                  aria-label="Clear search"
                >
                  ×
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="faq-grid">
          {filteredFaqs.length === 0 && (
            <div className="no-results">No matching questions.</div>
          )}
          {filteredFaqs.map((faq, visualIndex) => {
            const originalIndex = faqs.indexOf(faq);
            const isOpen = openSet.has(originalIndex);
            return (
              <RevealAnimation
                key={originalIndex}
                animation="fade"
                delay={visualIndex * 80}
                className={`faq-item ${visualIndex % 2 === 1 ? 'wide' : ''}`}
              >
                <div className={`faq-card ${isOpen ? "active" : ""}`}>
                  <button
                    className="faq-question"
                    onClick={() => toggleFAQ(originalIndex)}
                    aria-expanded={isOpen}
                    aria-controls={`faq-answer-${originalIndex}`}
                    id={`faq-question-${originalIndex}`}
                  >
                    <span className="question-meta">
                      <span className="q-index">{String(visualIndex + 1).padStart(2, '0')}</span>
                    </span>
                    <h3 className="question-text">{faq.question}</h3>
                    <span className="faq-icon" aria-hidden="true">
                      <svg
                        width="22"
                        height="22"
                        viewBox="0 0 24 24"
                        fill="none"
                        className={`icon ${isOpen ? "rotated" : ""}`}
                      >
                        <path
                          d="M12 5V19M5 12H19"
                          stroke="currentColor"
                          strokeWidth="2.2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                  </button>
                  <div
                    className={`faq-answer ${isOpen ? "expanded" : ""}`}
                    ref={(el) => (answerRefs.current[originalIndex] = el)}
                    id={`faq-answer-${originalIndex}`}
                    role="region"
                    aria-labelledby={`faq-question-${originalIndex}`}
                  >
                    <div className="answer-content">
                      {isPlainMode ? <p>{faq.answer}</p> : renderPortableText(faq.answer)}
                    </div>
                  </div>
                </div>
              </RevealAnimation>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
